import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormHelperText,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';


const useStyles = makeStyles(() => ({
    root: {},
}));


function LoginForm({ className, onSubmit, setShowInfoAlert, ...rest }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [showOrderNotFound, setShowOrderNotFound] = React.useState(false);

    const handleCloseDialog = () => {
        setShowOrderNotFound(false);
    };
    return (
        <>
            <Formik
                initialValues={{
                    dealerCode: '',
                    trackingNumber: ''
                }}
                validationSchema={Yup.object().shape({
                    dealerCode: Yup.string().required(t('dealerCodeIsRequired')),
                    trackingNumber: Yup.string().max(255).required(t('moparShipperNumberIsRequired'))
                })}
                validateOnMount={false}
                onSubmit={async (values, {
                    setErrors,
                    setStatus,
                    setSubmitting
                }) => {
                    try {
                        await onSubmit(values.dealerCode, values.trackingNumber);
                        setShowOrderNotFound(false);
                    } catch (error) {
                        // Show custom alert for order not found
                        setShowOrderNotFound(true);
                        setStatus({ success: false });
                        setSubmitting(false);
                    }
                    finally { setSubmitting(false) }
                }}
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                    isValid, // <-- add this
                }) => {

                    const handleFieldChange = (e) => {
                        handleChange(e);
                        const { name, value } = e.target;
                        // Check if either field has a value after change
                        const dealerCode = name === 'dealerCode' ? value : values.dealerCode;
                        const trackingNumber = name === 'trackingNumber' ? value : values.trackingNumber;
                        if (setShowInfoAlert && (dealerCode || trackingNumber)) {
                            setShowInfoAlert(false);
                        }
                    };
                    return (<form
                        noValidate
                        className={clsx(classes.root, className)}
                        onSubmit={handleSubmit}
                        {...rest}
                    >

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    error={Boolean(touched.dealerCode && errors.dealerCode)}
                                    fullWidth
                                    helperText={touched.dealerCode ? errors.dealerCode : ''}
                                    label={t('dealerCodeNumber')}
                                    name="dealerCode"
                                    onBlur={handleBlur}
                                    onChange={handleFieldChange}
                                    type="text"
                                    value={values.dealerCode}
                                    variant="outlined"
                                    disabled={isSubmitting}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    error={Boolean(touched.trackingNumber && errors.trackingNumber)}
                                    fullWidth
                                    helperText={touched.trackingNumber ? errors.trackingNumber : ''}
                                    label={t('moparShipperNumber')}
                                    name="trackingNumber"
                                    onBlur={handleBlur}
                                    onChange={handleFieldChange}
                                    type="text"
                                    value={values.trackingNumber}
                                    variant="outlined"
                                    disabled={isSubmitting}
                                />
                            </Grid>
                        </Grid>

                        <Box mt={2}>
                            {isSubmitting && (
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    alignContent={'center'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                >
                                    <CircularProgress style={{ marginRight: 10 }} />{' '}
                                    <Typography>{t('loggingIn')}</Typography>
                                </Box>
                            )}
                            {!isSubmitting && (
                                <Button
                                    color="secondary"
                                    disabled={isSubmitting || !isValid || !values.dealerCode || !values.trackingNumber} // <-- updated line
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                >
                                    {isSubmitting ? t('searching') : t('search')}
                                </Button>
                            )}
                            {errors.submit && (
                                <Box mt={3}>
                                    <FormHelperText error>{errors.submit}</FormHelperText>
                                </Box>
                            )}
                        </Box>
                    </form>)
                }}
            </Formik>
            <Dialog
                open={showOrderNotFound}
                onClose={handleCloseDialog}
                aria-labelledby="order-not-found-dialog-title"
                fullWidth // makes dialog take full width up to maxWidth
                PaperProps={{
                    style: { maxWidth: 550 } // set a custom min width in px
                }}
            >
                <DialogContent>
                    <DialogContentText>
                        {t('orderNotFoundMessage')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary" autoFocus
                        type="submit" variant="contained">
                        {t('ok')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

LoginForm.propTypes = {
    className: PropTypes.string,
    /**
     * Callback that runs after a user successfully logs in;
     * TODO: Currently cannot access hook functions here as they are
     * not run/evaluated in the context of react; running in the
     * context of the @nsd-fe doesn't allow React hooks to be run in them (?)
     */
    onSubmit: PropTypes.func,
    setShowInfoAlert: PropTypes.func,
    /**
     * Type of app this is, passed in from Routes.js
     */
    type: PropTypes.oneOf(['client', 'employee']),
};

LoginForm.defaultProps = {
    onSubmit: () => { },
};

export default LoginForm;


